import React from "react";
import { useTranslation } from "react-i18next";
import {
  HiOutlineDocumentText,
  HiUserGroup,
  HiOutlineCheckCircle,
  HiShieldCheck,
  HiClipboardList,
  HiLockClosed,
} from "react-icons/hi";
import GettingStarted from "../../molecules/GettingStarted";

const ESignatureGettingStarted: React.FC = () => {
  const { t } = useTranslation();

  const steps = [
    {
      icon: <HiOutlineDocumentText size={22} />,
      title: t("esignature.gettingStarted.steps.step1.title") as string,
      body: t("esignature.gettingStarted.steps.step1.body") as string,
      className: "sm:col-span-2 lg:col-span-1",
      mockup: (
        <div className="mt-3 bg-white border border-gray-200 rounded-md p-3 overflow-hidden">
          <div className="h-1.5 bg-gray-200 rounded w-3/4 mb-1.5" />
          <div className="h-1.5 bg-gray-200 rounded w-full mb-1.5" />
          <div className="h-1.5 bg-gray-200 rounded w-5/6 mb-3" />
          <div className="flex items-center gap-2 mt-3 min-w-0">
            <span className="text-xs uppercase tracking-wide text-gray-400 font-medium flex-shrink-0">
              {t("esignature.gettingStarted.steps.step1.mockupLabel")}
            </span>
            <span className="flex-1 border-b border-dashed border-gray-300 min-w-[20px]" />
          </div>
          <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-xs font-mono font-semibold max-w-full break-all">
            {`{{signature}}`}
          </div>
        </div>
      ),
      tip: t("esignature.gettingStarted.steps.step1.tip") as string,
    },
    {
      icon: <HiUserGroup size={22} />,
      title: t("esignature.gettingStarted.steps.step2.title") as string,
      body: t("esignature.gettingStarted.steps.step2.body") as string,
      mockup: (
        <div className="mt-3 bg-white border border-gray-200 rounded-md p-3 overflow-hidden">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
              A
            </div>
            <div className="min-w-0 flex-1">
              <div className="h-1.5 bg-gray-200 rounded w-1/2 mb-1" />
              <div className="h-1 bg-gray-100 rounded w-3/4" />
            </div>
          </div>
          <div className="mt-2.5 px-2 py-1 rounded bg-blue-50 border border-blue-100 text-xs text-blue-700 font-medium inline-flex items-start gap-1 max-w-full">
            <span className="flex-shrink-0">→</span>
            <span className="break-words min-w-0">
              {t("esignature.gettingStarted.steps.step2.mockupEmail")}
            </span>
          </div>
        </div>
      ),
    },
    {
      icon: <HiOutlineCheckCircle size={22} />,
      title: t("esignature.gettingStarted.steps.step3.title") as string,
      body: t("esignature.gettingStarted.steps.step3.body") as string,
      mockup: (
        <div className="mt-3 bg-white border border-gray-200 rounded-md p-3 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <HiOutlineDocumentText
                size={18}
                className="text-gray-400 flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="h-1.5 bg-gray-200 rounded w-16 sm:w-20 mb-1" />
                <div className="h-1 bg-gray-100 rounded w-10 sm:w-12" />
              </div>
            </div>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold flex-shrink-0">
              <HiOutlineCheckCircle size={12} />
              {t("esignature.gettingStarted.steps.step3.mockupBadge")}
            </span>
          </div>
        </div>
      ),
    },
  ];

  return (
    <GettingStarted
      icon={<HiOutlineDocumentText className="w-6 h-6 sm:w-7 sm:h-7" />}
      title={t("esignature.gettingStarted.title") as string}
      subtitle={t("esignature.gettingStarted.subtitle") as string}
      steps={steps}
      banner={
        <div className="mt-6 sm:mt-8 pt-6 border-t border-gray-100">
          <p className="text-xs uppercase tracking-wider text-gray-400 text-center mb-3 font-medium">
            {t("esignature.gettingStarted.trustTitle")}
          </p>
          <div className="flex flex-col xs:flex-row flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-gray-600">
            <span className="inline-flex items-center gap-1.5">
              <HiShieldCheck
                size={16}
                className="text-green-600 flex-shrink-0"
              />
              <span className="text-center">
                {t("esignature.upsell.legallyBinding")}
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <HiClipboardList
                size={16}
                className="text-blue-600 flex-shrink-0"
              />
              <span className="text-center">
                {t("esignature.upsell.auditTrail")}
              </span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <HiLockClosed size={16} className="text-gray-600 flex-shrink-0" />
              <span className="text-center">
                {t("esignature.upsell.secureStorage")}
              </span>
            </span>
          </div>
        </div>
      }
    />
  );
};

export default ESignatureGettingStarted;
